import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EbikLogo } from "@/components/EbikLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      const staff = ["super_admin", "managing_director", "operations_manager", "logistics_coordinator", "fleet_manager", "warehouse_manager", "finance_manager", "customer_service"];
      nav(data.user.role === "driver" ? "/app/driver" : staff.includes(data.user.role) ? "/app/control-tower" : "/app");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#0B0F17] text-white relative overflow-hidden grain">
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
        <Link to="/"><EbikLogo variant="dark" /></Link>
        <div className="relative z-10">
          <p className="eyebrow text-primary">Enterprise Logistics Platform</p>
          <h1 className="mt-4 text-4xl font-heading font-extrabold leading-tight">Moving Cargo.<br />Connecting Business.</h1>
          <p className="mt-4 text-slate-400 max-w-md">Real-time visibility across South Africa and the Southern African region. Control tower, fleet, warehousing and AI intelligence in one platform.</p>
        </div>
        <p className="relative z-10 text-xs font-mono text-slate-500">Cape Town · Johannesburg · Durban</p>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><EbikLogo /></div>
          <h2 className="text-2xl font-heading font-extrabold">Sign in to Ebik</h2>
          <p className="text-muted-foreground text-sm mt-1">Access your logistics command centre.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label>Email</Label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.co.za" required className="mt-1.5" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required className="mt-1.5" />
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-primary hover:bg-red-700 h-11">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          <p className="mt-4 text-sm text-muted-foreground">No account? <Link to="/register" className="text-primary font-semibold">Register your company</Link></p>
        </div>
      </div>
    </div>
  );
}
